import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import "./../styles/components/newsDetail.css";
import { PostsService, Post } from "../services/postsService";
import { toAbsoluteUrl } from "../config/api";
import { formatDate } from "../utils/dateFormatter";
import defaultThumbnail from "../assets/images/default-thumbnail.webp";

const NewsDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [news, setNews] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [highlightedPosts, setHighlightedPosts] = useState<Post[]>([]);
  const [relatedPosts, setRelatedPosts] = useState<Post[]>([]);
  
  // Lấy returnPage từ location state
  const returnPage = (location.state as { returnPage?: number })?.returnPage;

  // Scroll về đầu trang ngay khi vào trang chi tiết
  useEffect(() => {
    // Scroll ngay lập tức
    window.scrollTo(0, 0);
    // Đảm bảo scroll lại sau khi render
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 0);
    return () => clearTimeout(timer);
  }, [slug]);

  useEffect(() => {
    const fetchNews = async () => {
      if (!slug) {
        navigate("/");
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const postData = await PostsService.getPublicPostBySlugWithView(slug);
        setNews(postData);

        const highlightedResponse = await PostsService.getPublicPosts({
          limit: 8,
          category: postData.category?.name,
          sortBy: "views",
        });

        const relatedResponse = await PostsService.getPublicPosts({
          limit: 8,
          category: postData.category?.name,
        });

        const filteredHighlighted = highlightedResponse.posts
          .filter((post) => post.slug !== slug)
          .slice(0, 8);
        setHighlightedPosts(filteredHighlighted);

        const filteredRelated = relatedResponse.posts
          .filter((post) => post.slug !== slug)
          .slice(0, 8);
        setRelatedPosts(filteredRelated);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Có lỗi xảy ra khi tải bài viết"
        );
        navigate("/");
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="news-detail">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Đang tải bài viết...</p>
        </div>
      </div>
    );
  }

  if (error || !news) {
    return (
      <div className="news-detail">
        <div className="error-container">
          <p>Không thể tải bài viết. Vui lòng thử lại sau.</p>
          <button onClick={() => navigate("/")} className="btn-back">
            <ChevronLeft size={16} /> Quay lại trang chủ
          </button>
        </div>
      </div>
    );
  }

  const getNewsPath = (postSlug: string) => `/${postSlug}`;

  // Hàm rút ngắn URL để hiển thị
  const truncateUrl = (url: string, maxLength: number = 80): string => {
    if (url.length <= maxLength) {
      return url;
    }
    // Cố gắng giữ phần đầu và cuối của URL
    const start = url.substring(0, maxLength - 20);
    const end = url.substring(url.length - 15);
    return `${start}...${end}`;
  };

  // Hàm loại bỏ HTML tags và lấy text thuần
  const stripHtml = (html: string): string => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  // Lấy preview text (loại bỏ HTML và giới hạn độ dài)
  const getPreviewText = (htmlContent: string, maxLength: number = 80): string => {
    if (!htmlContent) return "";
    
    // Tạo một div tạm để parse HTML
    const tmp = document.createElement("DIV");
    tmp.innerHTML = htmlContent;
    
    // Loại bỏ các heading tags (h1, h2, h3, etc.) vì chúng thường chứa title
    const headings = tmp.querySelectorAll("h1, h2, h3, h4, h5, h6");
    headings.forEach((heading) => heading.remove());
    
    // Lấy text thuần sau khi đã loại bỏ headings
    let text = tmp.textContent || tmp.innerText || "";
    text = text.trim();
    
    // Nếu vẫn còn text, lấy preview
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength).trim() + "...";
  };

  // Hàm xử lý quay lại với currentPage được giữ nguyên
  const handleGoBack = () => {
    if (returnPage && returnPage > 1) {
      navigate(`/news?page=${returnPage}`);
    } else {
      navigate("/news");
    }
  };

  return (
    <div className="news-detail">
      <div className="action-back">
        <button className="btn-back" onClick={handleGoBack}>
          <ChevronLeft size={16} /> Quay lại
        </button>
      </div>
      <div className="news-container">
        <div className="main-content">
          <div className="center-article">
            <div className="news-article">
              <div className="article-header-news">
                <h1>{news.title}</h1>
              </div>
              {/* {news.featuredImage && (
                <div className="banner">
                  <img
                    src={toAbsoluteUrl(news.featuredImage)}
                    alt="Banner"
                    className="banner-image"
                  />
                </div>
              )} */}
              <div className="banner">
                <img
                  src={
                    news.featuredImage && news.featuredImage.trim()
                      ? news.featuredImage.startsWith('http')
                        ? news.featuredImage
                        : toAbsoluteUrl(news.featuredImage)
                      : defaultThumbnail
                  }
                  alt="Banner"
                  className="banner-image"
                  onError={(e) => (e.currentTarget.src = defaultThumbnail)}
                />
              </div>
              <div className="article-content">
                {/* {news.metaDescription && (
                  <p className="intro">{news.metaDescription}</p>
                )} */}
                <div dangerouslySetInnerHTML={{ __html: news.content }} />
                {news.externalLinks && news.externalLinks.length > 0 && (
                  <div className="news-detail-backlinks">
                    {/* <strong>Backlinks:</strong> */}
                    <ul>
                      {news.externalLinks.map((link, index) => (
                        <li key={index}>
                          <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={link}
                          >
                            {truncateUrl(link)}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="related-section">
            <div className="related-header">
              <h2>Bài Viết Liên Quan</h2>
              <a href="/news" className="custom-show-all">
                Xem thêm <ChevronRight size={16} />
              </a>
            </div>
            <div className="related-list">
              {relatedPosts.map((relatedNews) => (
                <Link
                  key={relatedNews.id}
                  to={getNewsPath(relatedNews.slug)}
                  className="related-item"
                >
                  {/* {relatedNews.featuredImage && (
                    <img
                      src={toAbsoluteUrl(relatedNews.featuredImage)}
                      alt={relatedNews.title}
                      className="related-image"
                    />
                  )} */}
                  <img
                    src={
                      relatedNews.featuredImage && relatedNews.featuredImage.trim()
                        ? relatedNews.featuredImage.startsWith('http')
                          ? relatedNews.featuredImage
                          : toAbsoluteUrl(relatedNews.featuredImage)
                        : defaultThumbnail
                    }
                    alt={relatedNews.title}
                    className="related-image"
                    onError={(e) => (e.currentTarget.src = defaultThumbnail)}
                  />
                  <h4>{relatedNews.title}</h4>
                  <p className="related-date">
                    {formatDate(relatedNews.createdAt)}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </div>
        <div className="right-sidebar">
          <div className="highlighted-section">
            {/* <div className="author-div">
              <div
                className="author-avatar-info"
                onClick={() =>
                  news.author &&
                  window.open(`/profile/${news.author.id}`, "_blank")
                }
                style={{ cursor: "pointer" }}
              >
                {news.author?.avatar ? (
                  <img
                    src={toAbsoluteUrl(news.author.avatar)}
                    alt={news.author.fullName || "Tác giả"}
                    className="news-detail-avatar"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      e.currentTarget.nextElementSibling?.classList.remove(
                        "hidden"
                      );
                    }}
                  />
                ) : null}
                <div
                  className={`news-detail-avatar-initials ${
                    news.author?.avatar ? "hidden" : ""
                  }`}
                  style={{ display: news.author?.avatar ? "none" : "flex" }}
                >
                  {news.author?.fullName
                    ? news.author.fullName
                        .split(" ")
                        .map((word) => word.charAt(0))
                        .join("")
                        .substring(0, 2)
                        .toUpperCase()
                    : "A"}
                </div>
              </div>
              <div className="author-actions">
                <div className="article-meta">
                  <span
                    onClick={() =>
                      news.author &&
                      window.open(`/profile/${news.author.id}`, "_blank")
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <UserRoundPen size={14} />{" "}
                    {news.author?.fullName || "Tác giả"}
                  </span>
                  <span>
                    <CalendarClock size={14} /> {formatDate(news.createdAt)}
                  </span>
                </div>
                <div className="meta-info">
                  {news.category && (
                    <span>
                      <Tag size={14} /> Loại: {news.category.name}
                    </span>
                  )}
                  <span className="view-count">
                    <Eye size={14} /> {news.viewCount} lượt xem
                  </span>
                </div>
              </div>
            </div> */}
            <div className="highlighted-list">
              <h2>Nổi Bật</h2>
              {highlightedPosts.map((item) => (
                <Link
                  key={item.id}
                  to={getNewsPath(item.slug)}
                  className="highlighted-item"
                >
                  {/* {item.featuredImage && (
                    <img
                      src={toAbsoluteUrl(item.featuredImage)}
                      alt={item.title}
                      className="highlighted-image"
                    />
                  )} */}
                  <img
                    src={
                      item.featuredImage && item.featuredImage.trim()
                        ? item.featuredImage.startsWith('http')
                          ? item.featuredImage
                          : toAbsoluteUrl(item.featuredImage)
                        : defaultThumbnail
                    }
                    alt={item.title}
                    className="highlighted-image"
                    onError={(e) => (e.currentTarget.src = defaultThumbnail)}
                  />
                  <div className="highlighted-content">
                    <h4>{item.title}</h4>
                    {item.content && (
                      <p className="highlighted-preview">
                        {getPreviewText(item.content, 80)}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsDetail;
